<template>
  <ElDropdown trigger="click" placement="bottom-end" @command="handleCommand">
    <button type="button" class="account-trigger" :title="`登入者：${greeting}`">
      <span>登入者：{{ greeting }}</span>
      <span aria-hidden="true">▾</span>
    </button>
    <template #dropdown>
      <ElDropdownMenu>
        <ElDropdownItem v-for="item in accountActions" :key="item.command" :command="item.command">
          {{ item.label }}
        </ElDropdownItem>
      </ElDropdownMenu>
    </template>
  </ElDropdown>

  <ElDialog
    v-model="dialogOpen"
    title="變更密碼"
    width="min(440px, calc(100vw - 32px))"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="!saving"
    :show-close="!saving"
    @closed="resetForm"
  >
    <form class="password-form" @submit.prevent="submitPassword">
      <p>新密碼至少需要 6 個字元，變更成功後請重新登入。</p>
      <label for="account-current-password">目前密碼</label>
      <input
        id="account-current-password"
        v-model="form.currentPassword"
        type="password"
        autocomplete="current-password"
        required
        :disabled="saving"
      />
      <label for="account-new-password">新密碼</label>
      <input
        id="account-new-password"
        v-model="form.newPassword"
        type="password"
        autocomplete="new-password"
        minlength="6"
        maxlength="128"
        required
        :disabled="saving"
      />
      <label for="account-confirm-password">確認新密碼</label>
      <input
        id="account-confirm-password"
        v-model="form.confirmPassword"
        type="password"
        autocomplete="new-password"
        minlength="6"
        maxlength="128"
        required
        :disabled="saving"
      />
      <p v-if="errorMessage" class="password-error" role="alert">{{ errorMessage }}</p>
      <div class="password-actions">
        <ElButton :disabled="saving" @click="dialogOpen = false">取消</ElButton>
        <ElButton type="primary" native-type="submit" :loading="saving">確認變更</ElButton>
      </div>
    </form>
  </ElDialog>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  ElButton,
  ElDialog,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElMessage,
} from 'element-plus'
import 'element-plus/dist/index.css'
import { isAxiosError } from 'axios'
import { authApi } from '@/api/auth'
import { apiErrorMessage } from '@/utils/apiErrorMessage'
import { useUserStore } from '@/stores/userStore'

defineProps<{ greeting: string }>()
const accountActions = [{ command: 'change-password', label: '變更密碼' }]
const router = useRouter()
const userStore = useUserStore()
const dialogOpen = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const form = reactive({ currentPassword: '', newPassword: '', confirmPassword: '' })

function resetForm() {
  Object.assign(form, { currentPassword: '', newPassword: '', confirmPassword: '' })
  errorMessage.value = ''
}

function handleCommand(command: string) {
  if (command === 'change-password') {
    resetForm()
    dialogOpen.value = true
  }
}

async function submitPassword() {
  if (saving.value) return
  errorMessage.value = ''
  if (form.newPassword !== form.confirmPassword) {
    errorMessage.value = '兩次輸入的新密碼不一致。'
    return
  }
  if (form.newPassword === form.currentPassword) {
    errorMessage.value = '新密碼不可與目前密碼相同。'
    return
  }
  saving.value = true
  try {
    await authApi.changePassword({ ...form })
  } catch (error) {
    errorMessage.value =
      (isAxiosError(error) && apiErrorMessage(error.response?.data)) || '變更密碼失敗，請稍後再試。'
    return
  } finally {
    saving.value = false
  }
  resetForm()
  dialogOpen.value = false
  userStore.logout()
  ElMessage.success('密碼已變更，請使用新密碼重新登入。')
  await router.replace('/login')
}
</script>

<style scoped>
.el-dropdown {
  min-width: 0;
}
.account-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  padding: 8px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #475569;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.account-trigger > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-trigger:hover,
.account-trigger:focus-visible {
  background: #eef8fc;
  border-color: #1a98c7;
}
.password-form {
  display: grid;
  gap: 10px;
}
.password-form p {
  margin: 0 0 8px;
}
.password-form input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font: inherit;
}
.password-form input:focus-visible {
  outline: 2px solid #1a98c7;
  outline-offset: 2px;
}
.password-error {
  color: #b91c1c;
}
.password-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
